import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { BITBUCKET_HOME, TOKEN_KINDS, TOKEN_SCOPES, tokenSteps, type ScmTokenKind } from './tokenGuideModel';

/** "One repository" (repository access token) or "Whole workspace" (workspace access token, Premium). */
export function TokenKindChoice({ kind, onChange }: { kind: ScmTokenKind; onChange: (kind: ScmTokenKind) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[13px]">What the token reaches</Label>
      <div role="radiogroup" aria-label="What the token reaches" className="grid grid-cols-2 gap-1 rounded-lg border bg-secondary p-1">
        {TOKEN_KINDS.map((o) => (
          <button
            key={o.kind}
            type="button"
            role="radio"
            aria-checked={kind === o.kind}
            data-testid={`scm-token-kind-${o.kind}`}
            onClick={() => onChange(o.kind)}
            className={cn('flex min-h-11 flex-col items-start justify-center rounded-md px-3 py-1.5 text-left transition-colors duration-150', kind === o.kind ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground')}
          >
            <span className="text-[13px] font-semibold">{o.label}</span>
            <span className="text-[11px] leading-snug text-subtle">{o.detail}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ScopeList() {
  return (
    <ul className="flex flex-col gap-1.5" data-testid="scm-token-scopes">
      {TOKEN_SCOPES.map((s) => (
        <li key={s.scope} className="flex items-start gap-2 text-xs">
          <Icon name={s.required ? 'check_circle' : 'add_circle'} size={15} className={cn('mt-px shrink-0', s.required ? 'text-succeeded' : 'text-subtle')} />
          <span className="min-w-0">
            <span className="font-semibold text-foreground">{s.label}</span>
            <span className="font-mono text-subtle"> ({s.scope})</span>
            <span className="text-muted-foreground"> · {s.required ? 'required' : 'for builds on push'}. {s.why}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Bitbucket in a new tab: the token is created before Hangar knows the workspace, so the steps say where to click. */
function OpenBitbucket() {
  return (
    <Button asChild size="sm" variant="outline" className="self-start">
      <a href={BITBUCKET_HOME} target="_blank" rel="noreferrer noopener" data-testid="scm-token-open-bitbucket">
        <Icon name="open_in_new" size={16} />
        Open Bitbucket
      </a>
    </Button>
  );
}

/** Which token to create, where, and with which permissions: the steps a person follows in Bitbucket before pasting it. */
export function BitbucketTokenGuide({ kind }: { kind: ScmTokenKind }) {
  return (
    <section aria-labelledby="scm-token-guide-title" data-testid="scm-token-guide" className="flex flex-col gap-3 rounded-lg border bg-card p-3 sm:p-4">
      <h3 id="scm-token-guide-title" className="text-[13px] font-semibold text-foreground">
        Create the {kind === 'repository' ? 'repository' : 'workspace'} access token in Bitbucket
      </h3>
      <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-xs leading-relaxed text-muted-foreground marker:text-subtle" data-testid="scm-token-steps">
        {tokenSteps(kind).map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <ScopeList />
      <OpenBitbucket />
    </section>
  );
}
